import { useEffect, useRef, useState } from 'react';
import { useActivityMonths, useMonthActivity, useSinceActivity } from '../../api/activity.ts';
import type { SectionKey } from '../../api/navigation.ts';
import {
  ACTIVITY_DEPARTMENTS, KIND_WORDS, agencyMonth, entriesSaid, minutesSaid, monthName, newlyOpen, timelinePlan,
  type ActivityEntry, type ActivityFilter, type ActivityKind, type TimelinePlan,
} from '../../activity.ts';
import { TaskCard } from '../../components/tasks/TaskCard.tsx';
import { VisitDetails } from '../../components/VisitDetails.tsx';
import { EntryCard } from '../../components/timeline/EntryCard.tsx';
import { LoadFailure } from '../../components/LoadFailure.tsx';
import { when } from '../../components/communications/CommunicationsFeed.tsx';
import { formatDate } from '../../dates.ts';
import { field, label } from '../../components/contacts/Sheet.tsx';
import { plain } from '../../components/tasks/Fields.tsx';
import { SectionPanel } from '../ClientRecord.tsx';

/**
 * The client's Timeline (A-#157): everything that has happened on the client, newest first, filtered by department
 * and kind. What is listed is the server's: each source read as the reader, so nobody sees an entry here they could
 * not already see. Completed work lives here; the client's Tasks list stays open work only.
 *
 * Deploy 8 (A-#227 S1): on a line, by month, with the year marked where it changes. The newest whole months are open
 * until at least 12 entries show (and the current month always); older months of this year are collapsed to their
 * count, earlier years to theirs. The counts are the server's, for the same reader and filters (GET .../activity/months);
 * a month's entries are read when it is opened. S2: every entry opens its card first (EntryCard; a task or an action
 * its TaskCard), and the card's "Open full screen" goes to the record's own place.
 */
export function TimelineSection({ clientId, sections }: { clientId: string; sections: readonly SectionKey[] }) {
  const [kind, setKind] = useState<ActivityKind | ''>('');
  const [department, setDepartment] = useState('');
  const [card, setCard] = useState<ActivityEntry | null>(null);
  const [visit, setVisit] = useState<string | null>(null);
  const filter: ActivityFilter = { ...(kind ? { kind } : {}), ...(department ? { department } : {}) };
  const q = useActivityMonths(clientId, filter);
  const plan = q.data ? timelinePlan(q.data.months, agencyMonth(new Date())) : null;
  const total = q.data?.months.reduce((s, m) => s + m.n, 0) ?? 0;
  return (
    <SectionPanel title="Timeline">
      <div className="flex flex-wrap gap-3 px-4 pb-3">
        <label className={label}>Department
          <select value={department} onChange={(e) => setDepartment(e.target.value)} className={field}>
            <option value="">All</option>
            {ACTIVITY_DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label className={label}>Kind
          <select value={kind} onChange={(e) => setKind(e.target.value as ActivityKind | '')} className={field}>
            <option value="">All</option>
            {(q.data?.kinds ?? []).map((k) => <option key={k} value={k}>{KIND_WORDS[k]}</option>)}
          </select>
        </label>
      </div>
      {q.error && !q.data ? (
        <LoadFailure what="The timeline" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching}
                     forbiddenIsSession={false} />
      ) : !plan ? (
        <p className="px-4 pb-3 text-neutral-600">Loading…</p>
      ) : total === 0 ? (
        <p className="px-4 pb-3 text-neutral-700">
          {kind || department ? 'Nothing matches these filters.' : 'Nothing has been recorded on this client yet.'}
        </p>
      ) : (
        // a new plan (filters changed) starts from its own opening rule
        <Line key={`${kind}|${department}`} plan={plan} clientId={clientId} filter={filter} sections={sections} onEntry={setCard} />
      )}
      {card && (card.kind === 'task' || card.kind === 'action'
        ? <TaskCard taskId={card.ref} onClose={() => setCard(null)} />
        : <EntryCard e={card} clientId={clientId} sections={sections} onClose={() => setCard(null)}
                     onVisitWhole={(id) => { setCard(null); setVisit(id); }} />)}
      {visit && <VisitDetails id={visit} onClose={() => setVisit(null)} />}
    </SectionPanel>
  );
}

/** The line: a marker for each year, its months under it, each open or collapsed to its count. */
function Line({ plan, clientId, filter, sections, onEntry }: {
  plan: TimelinePlan; clientId: string; filter: ActivityFilter; sections: readonly SectionKey[];
  onEntry: (e: ActivityEntry) => void;
}) {
  const [openMonths, setOpenMonths] = useState<Set<string>>(() => new Set(plan.open));
  const [openYears, setOpenYears] = useState<Set<string>>(() => new Set(plan.years.filter((y) => y.open).map((y) => y.year)));
  // A-#239: the months open at first are read as ONE page, from the oldest of them on; any other month, when opened
  const [seeded, setSeeded] = useState<Set<string>>(() => new Set(plan.open));
  // A-#238: a month a later plan opens (the counts read again while the Timeline is open) opens too, and is read with them
  const planned = useRef<readonly string[]>(plan.open);
  const openKey = plan.open.join(',');
  useEffect(() => {
    const add = newlyOpen(planned.current, plan.open);
    planned.current = plan.open;
    if (add.length === 0) return;
    setOpenMonths((s) => new Set([...s, ...add]));
    setSeeded((s) => new Set([...s, ...add]));
    setOpenYears((s) => new Set([...s, ...add.map((m) => m.slice(0, 4))]));
  }, [openKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const since = [...seeded].sort()[0]!;
  const first = useSinceActivity(clientId, filter, since);
  const firstEntries = first.data?.pages.flatMap((p) => p.entries) ?? [];
  // the one month where the first page ran out before that month's count: its "Show earlier"
  const short = plan.years.flatMap((y) => y.months)
    .find((m) => seeded.has(m.month) && openMonths.has(m.month) && firstEntries.filter((e) => e.month === m.month).length < m.n)?.month;
  const toggle = (set: Set<string>, put: (s: Set<string>) => void, k: string) => {
    const n = new Set(set); if (n.has(k)) n.delete(k); else n.add(k); put(n);
  };
  const current = agencyMonth(new Date());
  return (
    <ol aria-label="What has happened, by month, newest first" className="ml-4 border-l-2 border-line pr-4 pb-3">
      {plan.years.map((y) => (
        <li key={y.year} className="relative pl-5">
          <span aria-hidden className="absolute top-3 -left-[0.45rem] h-3 w-3 rounded-full bg-accent" />
          {openYears.has(y.year) ? (
            <h3 className="pt-2 text-sm font-bold tracking-[0.12em] text-neutral-700 uppercase">{y.year}</h3>
          ) : (
            <button type="button" className="min-h-[44px] text-left font-semibold text-brand-text underline"
                    aria-expanded={false} onClick={() => toggle(openYears, setOpenYears, y.year)}>
              {y.year} · {entriesSaid(y.n)}
            </button>
          )}
          {openYears.has(y.year) && (
            <ol aria-label={`${y.year}, by month`}>
              {y.months.map((m) => (
                <li key={m.month} data-month={m.month} className="py-1">
                  {openMonths.has(m.month) ? (
                    <>
                      <button type="button" className="min-h-[44px] text-left font-semibold text-neutral-900"
                              aria-expanded onClick={() => toggle(openMonths, setOpenMonths, m.month)}>
                        {monthName(m.month)} {m.month.slice(0, 4)}
                      </button>
                      {m.n === 0 ? (
                        <p className="pb-2 text-sm text-neutral-600">{m.month === current ? 'Nothing yet this month.' : 'Nothing this month.'}</p>
                      ) : seeded.has(m.month) ? (
                        <Seeded first={first} entries={firstEntries.filter((e) => e.month === m.month)} month={m.month}
                                short={short === m.month} sections={sections} onEntry={onEntry} />
                      ) : (
                        <Month clientId={clientId} filter={filter} month={m.month} sections={sections} onEntry={onEntry} />
                      )}
                    </>
                  ) : (
                    <button type="button" className="min-h-[44px] text-left font-semibold text-brand-text underline"
                            aria-expanded={false} onClick={() => toggle(openMonths, setOpenMonths, m.month)}>
                      {monthName(m.month)} · {entriesSaid(m.n)}
                    </button>
                  )}
                </li>
              ))}
            </ol>
          )}
        </li>
      ))}
    </ol>
  );
}

/** A month opened at first: its share of the one first page, and "Show earlier" where that page ran out. */
function Seeded({ first, entries, month, short, sections, onEntry }: {
  first: ReturnType<typeof useSinceActivity>; entries: ActivityEntry[]; month: string; short: boolean;
  sections: readonly SectionKey[]; onEntry: (e: ActivityEntry) => void;
}) {
  if (first.error && !first.data) {
    return <LoadFailure what="The timeline" error={first.error} onRetry={() => void first.refetch()} retrying={first.isFetching} forbiddenIsSession={false} />;
  }
  if (!first.data) return <p className="pb-2 text-sm text-neutral-600">Loading…</p>;
  return (
    <>
      <ol aria-label={`${monthName(month)} ${month.slice(0, 4)}, newest first`} className="divide-y divide-neutral-200">
        {entries.map((e) => <Row key={`${e.kind}:${e.id}`} e={e} sections={sections} onEntry={onEntry} />)}
      </ol>
      {short && first.hasNextPage && (
        <button type="button" className={plain} disabled={first.isFetchingNextPage} onClick={() => void first.fetchNextPage()}>
          {first.isFetchingNextPage ? 'Loading…' : 'Show earlier'}
        </button>
      )}
    </>
  );
}

/** A month opened later: its own entries, read when it opens, paged within the month. */
function Month({ clientId, filter, month, sections, onEntry }: {
  clientId: string; filter: ActivityFilter; month: string; sections: readonly SectionKey[]; onEntry: (e: ActivityEntry) => void;
}) {
  const q = useMonthActivity(clientId, filter, month);
  const entries = q.data?.pages.flatMap((p) => p.entries) ?? [];
  if (q.error && !q.data) {
    return <LoadFailure what="This month" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false} />;
  }
  if (!q.data) return <p className="pb-2 text-sm text-neutral-600">Loading…</p>;
  return (
    <>
      <ol aria-label={`${monthName(month)} ${month.slice(0, 4)}, newest first`} className="divide-y divide-neutral-200">
        {entries.map((e) => <Row key={`${e.kind}:${e.id}`} e={e} sections={sections} onEntry={onEntry} />)}
      </ol>
      {q.hasNextPage && (
        <button type="button" className={plain} disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
          {q.isFetchingNextPage ? 'Loading…' : 'Show earlier this month'}
        </button>
      )}
    </>
  );
}

function Row({ e, onEntry }: { e: ActivityEntry; sections: readonly SectionKey[]; onEntry: (e: ActivityEntry) => void }) {
  const what = e.what ?? KIND_WORDS[e.kind];
  const spent = minutesSaid(e.minutes);
  return (
    <li className="py-3">
      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-neutral-700">
        <span className="font-semibold text-neutral-900">{e.at ? when(e.at) : e.on ? formatDate(e.on) : 'Date not known'}</span>
        <span className="rounded bg-note-bg px-1.5 py-0.5 text-note-fg">{KIND_WORDS[e.kind]}</span>
        {e.department && <span className="rounded bg-tint px-1.5 py-0.5 font-semibold text-brand-text">{e.department}</span>}
        {e.edited && <span className="font-semibold text-neutral-800">Edited</span>}
      </p>
      <p className="mt-1 text-neutral-900">
        {/* deploy 8 (S2): every entry opens its card first */}
        <button type="button" className="min-h-[44px] text-left font-semibold text-brand-text underline" onClick={() => onEntry(e)}>{what}</button>
      </p>
      <p className="text-sm text-neutral-700">
        {[e.by ? `By ${e.by}` : 'By someone not on record', spent ? `${spent} spent` : null].filter(Boolean).join(' · ')}
      </p>
      {e.note && <p className="mt-1 whitespace-pre-line text-neutral-800">{e.note}</p>}
    </li>
  );
}
